import { defineStore } from 'pinia';
import {
  useCreateTopicInstanceMutation,
  useRemoveTopicInstanceMutation,
  useReorderTopicInstanceMutation,
  useUpdateTopicInstanceMutation,
} from '~/composables';

import { type TopicInstance, TopicInstancesDocument } from '~/graphql';

export const topicInstancesCacheExchange = {
  optimistic: {
    updateTopicInstance(variables, cache, _info) {
      return {
        __typename: 'TopicInstanceResult',
        topicInstance: {
          __typename: 'TopicInstance',
          ..._info.variables,
          ...variables.updateTopicInstanceInput,
        },
      };
    },
  },
  updates: {
    Mutation: {
      //createTopicInstance(result, _args, cache, _info)
      createTopicInstance(result, _args, cache) {
        const courseId = _args.createTopicInstanceInput.courseId;

        cache.updateQuery(
          {
            query: TopicInstancesDocument,
            variables: {
              courseIdInput: { id: courseId },
            },
          },
          (data) => {
            if (data) {
              result.createTopicInstance.topicInstance.isNew = true;
              data.topicInstances.push({
                ...result.createTopicInstance.topicInstance,
              });
            }
            return data;
          }
        );
      },
      reorderTopicInstance(result, _args, cache) {
        const courseId = result.reorderTopicInstance.topicInstance.course.id;
        const topicInstanceId = _args.reorderTopicInstanceInput.id;
        const position = _args.reorderTopicInstanceInput.position;

        cache.updateQuery(
          {
            query: TopicInstancesDocument,
            variables: {
              courseIdInput: { id: courseId },
            },
          },
          (data) => {
            if (data) {
              for (let i = 0; i < data.topicInstances.length; i++) {
                if (data.topicInstances[i]?.id === topicInstanceId) {
                  data.topicInstances.splice(i, 1);
                }
              }
              data.topicInstances.splice(position, 0, result.reorderTopicInstance.topicInstance);
            }
            return data;
          }
        );
      },
      removeTopicInstance(result, _args, cache) {
        const topicInstanceId = _args.removeTopicInstanceInput.id;
        cache.invalidate({ __typename: 'TopicInstance', id: topicInstanceId });
      },
    },
  },
};

export const useTopicInstanceManagement = defineStore('topicInstanceManagement', () => {
  const createTopicInstanceMutation = useCreateTopicInstanceMutation();
  const updateTopicInstanceMutation = useUpdateTopicInstanceMutation();
  const removeTopicInstanceMutation = useRemoveTopicInstanceMutation();
  const reorderTopicInstanceMutation = useReorderTopicInstanceMutation();

  const createTopicInstance = async (courseId: string, name: string, position: number) => {
    const result = await createTopicInstanceMutation.executeMutation({
      createTopicInstanceInput: {
        courseId,
        name,
        position,
        expanded: true,
      },
    });
    if (result.error) {
      throw result.error;
    }
    return result.data?.createTopicInstance.topicInstance;
  };

  const updateTopicInstance = async (
    topicInstanceId: string,
    updatedFields: object,
    topicInstance: TopicInstance
  ) => {
    const result = await updateTopicInstanceMutation.executeMutation(
      {
        updateTopicInstanceInput: {
          id: topicInstanceId,
          ...updatedFields,
        },
      },
      topicInstance
    );
    if (result.error) {
      throw result.error;
    }
    return result.data?.updateTopicInstance.topicInstance;
  };

  const removeTopicInstance = async (topicInstanceId: string, forceDelete: boolean) => {
    const result = await removeTopicInstanceMutation.executeMutation({
      topicInstanceIdInput: {
        id: topicInstanceId,
        forceDelete,
      },
    });
    if (result.error) {
      throw result.error;
    }
    return result.data?.removeTopicInstance.done;
  };

  const reorderTopicInstance = async (topicInstanceId: string, position: number) => {
    await reorderTopicInstanceMutation.executeMutation({
      reorderTopicInstanceInput: {
        id: topicInstanceId,
        position,
      },
    });
  };

  return {
    createTopicInstance,
    updateTopicInstance,
    removeTopicInstance,
    reorderTopicInstance,
  };
});
